import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/content';
import { EASE } from './Reveal';

export function Hero() {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const yArt = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const lineVariants = {
    hidden: { y: '115%' },
    show: (i) => ({
      y: '0%',
      transition: { duration: 0.9, ease: EASE, delay: 0.15 + i * 0.13 },
    }),
  };

  return (
    <section
      id="beranda"
      ref={ref}
      className="relative overflow-hidden pt-24 pb-14 sm:pt-28 lg:pt-40 lg:pb-24"
    >
      <motion.div
        style={{ y: yArt }}
        aria-hidden
        className="pointer-events-none absolute inset-0 [mask-image:linear-gradient(to_right,transparent_0%,black_52%)]"
        data-testid="hero-art"
      >
        <motion.img
          src="/images/hero-ribbon.jpg"
          alt=""
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0 h-full w-full object-cover object-right opacity-60 [mask-image:linear-gradient(to_bottom,transparent_0%,black_18%,black_82%,transparent_100%)] sm:opacity-75 lg:opacity-100"
        />
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#0A0A0C] to-transparent"
        />
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0A0A0C] to-transparent"
        />
        <div className="absolute inset-y-0 right-[5%] hidden items-center md:flex lg:right-[7%]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
            className="relative text-right opacity-90 [filter:drop-shadow(0_2px_20px_rgba(0,0,0,0.7))]"
          >
            <p className="text-silver font-display text-4xl font-extrabold tracking-[0.06em] lg:text-5xl">
              {t.hero.wordmark}
            </p>
            <ul className="mt-2.5 flex flex-col gap-1 font-mono text-[9px] font-medium tracking-[0.45em] text-slate-400 lg:text-[10px]">
              {t.hero.words.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </motion.div>
        </div>
      </motion.div>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#0A0A0C_26%,rgba(10,10,12,0.45)_48%,transparent_66%)]"
      />
      <div
        aria-hidden
        className="halftone pointer-events-none absolute left-0 top-24 hidden h-64 w-40 opacity-60 [mask-image:linear-gradient(to_right,black,transparent)] lg:block"
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-8 lg:px-10">
        <div className="max-w-xl lg:max-w-[46%]">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-mono text-[11px] font-semibold uppercase tracking-[0.35em] text-nofina-red sm:text-xs"
            data-testid="hero-eyebrow"
          >
            {t.hero.eyebrow}
          </motion.p>

          <h1
            key={`hero-title-${lang}`}
            data-testid="hero-headline"
            className="mt-5 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-[3.2rem]"
          >
            {t.hero.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                >
                  {line.map((seg, j) =>
                    seg.a ? (
                      <span key={j} className="text-nofina-red">
                        {seg.t}
                      </span>
                    ) : (
                      <span key={j}>{seg.t}</span>
                    )
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-subtitle"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-8 flex flex-col gap-3.5 sm:flex-row sm:items-center"
          >
            <a
              href={t.hero.ctaPrimary.href}
              data-testid="hero-primary-cta"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-nofina-red px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-nofina-redhover hover:shadow-[0_0_36px_rgba(229,9,20,0.45)]"
            >
              {t.hero.ctaPrimary.label}
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={t.hero.ctaSecondary.href}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/[0.04] px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:border-white/50 hover:bg-white/[0.08]"
            >
              {t.hero.ctaSecondary.label}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
